import { useState, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { springConfig, type AuthTheme } from './constants';

interface AnimatedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
  theme?: AuthTheme;
}

export const AnimatedInput = forwardRef<HTMLInputElement, AnimatedInputProps>(
  ({ label, error, icon, showPasswordToggle, type, className, theme = 'invol', ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const inputType = showPasswordToggle && showPassword ? 'text' : type;

    // Theme-aware colors
    const focusIconColor = theme === 'drboese' ? 'text-yellow-500' : 'text-blue-500';
    const focusBorderColor = theme === 'drboese' ? 'focus:border-yellow-400' : 'focus:border-blue-400';
    const focusRingColor = theme === 'drboese' ? 'focus:ring-yellow-500/10' : 'focus:ring-blue-500/10';
    const focusShadowColor = theme === 'drboese'
      ? 'rgba(255, 211, 52, 0.15)'
      : 'rgba(59, 130, 246, 0.1)';

    return (
      <motion.div
        animate={error ? { x: [0, -8, 8, -8, 8, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="space-y-2"
      >
        <Label
          htmlFor={props.id}
          className="text-sm font-medium text-slate-700"
        >
          {label}
        </Label>
        <div className="relative">
          {/* Icon */}
          {icon && (
            <div
              className={cn(
                'absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors duration-200',
                isFocused ? focusIconColor : 'text-slate-400'
              )}
            >
              {icon}
            </div>
          )}

          {/* Input */}
          <motion.input
            ref={ref}
            {...props}
            type={inputType}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            className={cn(
              'w-full rounded-xl border bg-white px-4 py-3 text-sm transition-all duration-200',
              'placeholder:text-slate-400',
              'focus:outline-none',
              icon && 'pl-11',
              showPasswordToggle && 'pr-11',
              error
                ? 'border-red-300 focus:border-red-400 focus:ring-4 focus:ring-red-500/10'
                : `border-slate-200 ${focusBorderColor} focus:ring-4 ${focusRingColor}`,
              className
            )}
            style={{
              boxShadow: isFocused
                ? error
                  ? '0 0 0 4px rgba(239, 68, 68, 0.1)'
                  : `0 0 0 4px ${focusShadowColor}`
                : 'none',
            }}
          />

          {/* Password toggle */}
          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
              tabIndex={-1}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={showPassword ? 'hide' : 'show'}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </motion.div>
              </AnimatePresence>
            </button>
          )}
        </div>

        {/* Error message */}
        <AnimatePresence>
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={springConfig.stiff}
              className="text-xs text-red-500 font-medium"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

AnimatedInput.displayName = 'AnimatedInput';
